import { AfterContentInit, Directive, ElementRef, HostBinding, inject, OnDestroy, Renderer2 } from '@angular/core';

import { delay, EMPTY, filter, fromEvent, Subscription, switchMap } from 'rxjs';
import { tap, throttleTime } from 'rxjs/operators';

import { DroppableService } from '../drop-files/droppable.service';

import { readDroppedEntries } from './read-dropped-entries';

@Directive({ selector: '[bnWebUiDroppable]' })
export class DroppableDirective implements AfterContentInit, OnDestroy {
  private readonly renderer = inject(Renderer2);
  private readonly host = inject(ElementRef);

  @HostBinding('class.bn-is-dragover') fileOver = false;

  private subscriptions = new Subscription();
  private get hostElement(): HTMLElement {
    return this.host.nativeElement;
  }
  private readonly overlayElement!: HTMLElement;
  private readonly droppableService = inject(DroppableService);

  constructor() {
    // Create overlay element - this way you don't need to change html if you are moving directive to another element in the future
    this.overlayElement = this.renderer.createElement('div');
    this.renderer.addClass(this.overlayElement, 'bn-droppable-overlay');
    this.renderer.appendChild(this.hostElement, this.overlayElement);
  }

  public ngAfterContentInit(): void {
    this.subscriptions.add(
      fromEvent<DragEvent>(this.hostElement, 'dragover')
        .pipe(
          filter((data) => !!data?.dataTransfer?.types.includes('Files') && this.droppableService.hasSubscribers()),
          tap((event) => event.preventDefault()),
          throttleTime(500, undefined, { leading: true }),
        )
        .subscribe(() => this.showDroppable()),
    );

    if (this.overlayElement) {
      this.subscriptions.add(
        fromEvent<DragEvent>(this.overlayElement, 'dragleave')
          .pipe(
            filter((data) => !!data?.dataTransfer?.types.includes('Files') && this.droppableService.hasSubscribers()),
            tap((event) => event.preventDefault()),
          )
          .subscribe(() => this.hideDroppable()),
      );

      this.subscriptions.add(
        fromEvent<DragEvent>(this.overlayElement, 'drop')
          .pipe(
            filter(() => this.droppableService.hasSubscribers()),
            tap((event) => event.preventDefault()),
            switchMap((event) => {
              if (!event?.dataTransfer?.items.length) return EMPTY;

              return readDroppedEntries(event.dataTransfer.items);
            }),
            tap((files) => {
              if (files.length) {
                this.droppableService.drop(files);
              }
            }),
            delay(200),
          )
          .subscribe(() => {
            this.hideDroppable();
          }),
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private hideDroppable(): void {
    this.fileOver = false;
  }

  private showDroppable(): void {
    this.fileOver = true;
  }
}
